import { useCallback, useEffect, useRef, useState } from 'react';
import { StayPublicError } from '../../../../../api/stays-public-client';
import { GuestGuideBody } from '../../../../../interfaces/stay-guide';
import { asGuideBody, GuideScreen, nextBoundaryMs, photoExpiryMs, screenOf } from './guide-state';

/**
 * THE GUIDE'S DATA, KEPT TRUE OVER TIME (plan S7.7, "Boundary refresh").
 *
 * The server decides every release; the page only asks again at the right
 * moments so the codes APPEAR at the reveal and VANISH at the expiry without
 * a reload:
 *
 *   · after every load, ONE timer to the next boundary the answer can cross
 *     (opens_at, reveal_at, check_in_at, the start of the check-out day,
 *     check_out_at, closes_at; a PIN lock's end) — or a little before the
 *     shortest photo URL runs out, whichever is first. setTimeout cannot wait
 *     longer than ~24.8 days, so the wait is capped at 24 days (the timer then
 *     simply asks again and re-arms);
 *   · on visibilitychange → visible (a phone unlocked, the tab came back:
 *     timers do not run in a frozen tab);
 *   · `ended` and 404 REPLACE the answer: the previous body — and with it every
 *     access value — is dropped from memory, never kept "for later".
 *
 * A quiet refetch (a boundary, the tab coming back) that fails for a reason
 * other than 404 keeps what is on screen and asks once more after a minute;
 * a first load that fails shows its screen (error / limited / invalid).
 */

export const MAX_WAIT_MS = 24 * 24 * 60 * 60 * 1000;
/** Ask a second after the boundary, so the server is on the far side of it. */
export const BOUNDARY_SLACK_MS = 1000;
/** Re-ask this long before the shortest-lived photo URL dies. */
export const PHOTO_LEAD_MS = 30_000;
/** A failed quiet refetch tries again after the limiter's minute. */
export const QUIET_RETRY_MS = 60_000;

export interface GuideResource {
  screen: GuideScreen | 'loading';
  body: GuestGuideBody | null;
  /** A quiet refetch is in flight while the content stays on screen. */
  refreshing: boolean;
  /** Ask again: `quiet` keeps the content on screen meanwhile. */
  reload: (quiet?: boolean) => void;
}

interface Held {
  screen: GuideScreen | 'loading';
  body: GuestGuideBody | null;
}

const statusOf = (err: unknown): number => (err instanceof StayPublicError ? err.status : 0);

type TimerRef = { current: ReturnType<typeof setTimeout> | null };

const stop = (timer: TimerRef) => {
  if (timer.current) clearTimeout(timer.current);
  timer.current = null;
};

export const useGuideBoundaries = (load: () => Promise<GuestGuideBody>): GuideResource => {
  const [held, setHeld] = useState<Held>({ screen: 'loading', body: null });
  const [refreshing, setRefreshing] = useState(false);
  const seq = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const screenRef = useRef<GuideScreen | 'loading'>('loading');
  const loadRef = useRef(load);
  loadRef.current = load;

  const run = useCallback((quiet: boolean) => {
    const mine = ++seq.current;
    const clear = () => stop(timer);
    const show = (next: Held) => {
      screenRef.current = next.screen;
      setHeld(next);
    };
    clear();
    if (quiet) setRefreshing(true);
    else show({ screen: 'loading', body: null });

    const arm = (delay: number) => {
      clear();
      timer.current = setTimeout(() => {
        timer.current = null;
        run(true);
      }, Math.max(0, Math.min(MAX_WAIT_MS, delay)));
    };

    loadRef
      .current()
      .then(data => {
        if (mine !== seq.current) return;
        setRefreshing(false);
        const screen = screenOf({ ok: true, body: data });
        const body = screen === 'error' ? null : asGuideBody(data);
        show({ screen, body });
        if (!body) return;
        const now = Date.now();
        const boundary = nextBoundaryMs(body, now);
        const photos = photoExpiryMs(body, now);
        const targets = [
          boundary !== null ? boundary + BOUNDARY_SLACK_MS : null,
          photos !== null ? photos - PHOTO_LEAD_MS : null,
        ].filter((v): v is number => v !== null);
        if (targets.length) arm(Math.min(...targets) - now);
      })
      .catch(err => {
        if (mine !== seq.current) return;
        setRefreshing(false);
        const screen = screenOf({ ok: false, status: statusOf(err) });
        // 404 always replaces the page (and drops the body). A quiet miss of
        // any other kind keeps the content and asks once more in a minute.
        if (quiet && screen !== 'invalid' && screenRef.current !== 'loading') {
          arm(QUIET_RETRY_MS);

          return;
        }
        show({ screen, body: null });
      });
  }, []);

  useEffect(() => {
    run(false);

    return () => {
      seq.current += 1;
      stop(timer);
    };
  }, [run]);

  useEffect(() => {
    const onVisible = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
      const s = screenRef.current;
      // Nothing to refresh on a dead link; the limited page waits its minute;
      // a first load is already on its way.
      if (s === 'invalid' || s === 'limited' || s === 'loading') return;
      run(true);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [run]);

  const reload = useCallback((quiet = false) => run(quiet), [run]);

  return { screen: held.screen, body: held.body, refreshing, reload };
};
